import { z } from 'zod';

// Centralized validation patterns for reusability
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
export const PASSWORD_REGEX_MESSAGE = 'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character';

export const USERNAME_REGEX = /^[a-zA-Z0-9_.-]+$/;
export const USERNAME_REGEX_MESSAGE = 'Username can only contain letters, numbers, dots, hyphens, and underscores';

export const OTP_REGEX = /^\d+$/;
export const OTP_LENGTH = 6;

// Reusable password validation schema
export const passwordValidation = z.string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .regex(PASSWORD_REGEX, PASSWORD_REGEX_MESSAGE);

// Reusable username validation schema
export const usernameValidation = z.string()
  .min(3, 'Username must be at least 3 characters')
  .max(50, 'Username must be less than 50 characters')
  .regex(USERNAME_REGEX, USERNAME_REGEX_MESSAGE);

// Reusable email validation schema
export const emailValidation = z.string()
  .email('Invalid email format')
  .refine(email => email.endsWith('@erpamperedeals.com'), {
    message: 'Email must be from @erpamperedeals.com domain',
  });

// Reusable OTP validation schema
export const otpValidation = z.string()
  .length(OTP_LENGTH, `OTP code must be ${OTP_LENGTH} digits`)
  .regex(OTP_REGEX, 'OTP code must contain only digits');

// Helper function to create password confirmation schema
export const createPasswordConfirmationSchema = (passwordFieldName: string = 'password', confirmFieldName: string = 'confirmPassword') => {
  return z.object({
    [passwordFieldName]: passwordValidation,
    [confirmFieldName]: z.string().min(1, 'Please confirm your password'),
  }).refine((data: any) => data[passwordFieldName] === data[confirmFieldName], {
    message: "Passwords don't match",
    path: [confirmFieldName],
  });
};

export const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  remember_me: z.boolean().optional(),
});

export const loginWithOTPSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  otp_code: otpValidation,
});

export const signupSchema = z.object({
  username: usernameValidation,
  password: passwordValidation,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  full_name: z.string().min(1, 'Full name is required'),
  email: emailValidation,
  invite_token: z.string().min(1, 'Invite token is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const inviteSchema = z.object({
  email: emailValidation,
  role: z.string().min(1, 'Role is required'),
  supervisor_id: z.string().optional(),
}).refine((data) => {
  const rolesRequiringSupervisor = ['agent', 'processor_agent', 'sales_team_lead', 'processor_team_lead', 'floor_manager', 'director'];
  if (rolesRequiringSupervisor.includes(data.role) && !data.supervisor_id) {
    return false;
  }
  return true;
}, {
  message: "Supervisor is required for this role",
  path: ["supervisor_id"],
});

export const resetPasswordSchema = z.object({
  newPassword: passwordValidation,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type LoginFormData = z.infer<typeof loginSchema>;
export type LoginWithOTPFormData = z.infer<typeof loginWithOTPSchema>;
export type SignupFormData = z.infer<typeof signupSchema>;
export type InviteFormData = z.infer<typeof inviteSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;